import { useEffect } from "react";
import { MapPin, ArrowUpRight, Phone, MessageSquare } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { HiringBanner } from "@/components/site/HiringBanner";
import { Services } from "@/components/site/Services";
import { OurPromise } from "@/components/site/OurPromise";
import { BottomBar } from "@/components/site/BottomBar";
import { Reveal } from "@/components/site/motion";
import { serviceAreas } from "@/data/site";

const metros = {
  "dallas-fort-worth": {
    name: "Dallas–Fort Worth",
    title: "Labor-Only Movers in Dallas–Fort Worth | Duke Summers Moving",
    description:
      "Labor-only movers in Dallas–Fort Worth. Loading, unloading, furniture moving, and packing help for local moves. Starting at $50 per mover, per hour. Request a quote today.",
    ogTitle: "Moving Labor in Dallas–Fort Worth, TX | Duke Summers Moving",
    ogUrl: "https://dukesummersmoving.com/dallas-fort-worth",
  },
  memphis: {
    name: "Memphis",
    title: "Labor-Only Movers in Memphis | Duke Summers Moving",
    description:
      "Labor-only movers in Memphis. Loading, unloading, furniture moving, and packing help for local moves. Starting at $50 per mover, per hour. Request a quote today.",
    ogTitle: "Moving Labor in Memphis, TN | Duke Summers Moving",
    ogUrl: "https://dukesummersmoving.com/memphis",
  },
};

const setMeta = (attr, key, content) => {
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

export default function CityLanding({ metro }) {
  const m = metros[metro];
  const area = serviceAreas.find((a) =>
    a.region.toLowerCase().includes(m.name.toLowerCase())
  );

  useEffect(() => {
    document.title = m.title;
    setMeta("name", "description", m.description);
    setMeta("property", "og:title", m.ogTitle);
    setMeta("property", "og:description", m.description);
    setMeta("property", "og:url", m.ogUrl);
    setMeta("name", "twitter:title", m.ogTitle);
    setMeta("name", "twitter:description", m.description);
    window.scrollTo(0, 0);
  }, [m]);

  return (
    <div data-testid={`city-page-${metro}`} className="bg-[#1C1C1C] min-h-screen">
      <Header />
      <main className="pt-32">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
              Now Serving {area?.region}
            </p>
            <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl max-w-4xl">
              Labor-Only Movers in {m.name}
            </h1>
            <div className="mt-8 flex flex-col gap-4">
              <a
                href="/#quote"
                data-testid="city-quote-btn"
                className="group inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#1C1C1C] font-bold px-8 py-4 text-base hover:bg-[#E03C00] transition-colors"
              >
                Request a Quote
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="tel:+19729476017"
                  data-testid="city-call-now-btn"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-4 sm:px-8 py-4 text-base whitespace-nowrap hover:bg-white hover:text-[#1C1C1C] transition-colors"
                >
                  <Phone className="h-5 w-5" strokeWidth={1.75} />
                  Call Now
                </a>
                <a
                  href="sms:+19729476017?&body=Hi%2C%20I%27d%20like%20a%20moving%20quote."
                  data-testid="city-text-us-btn"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-4 sm:px-8 py-4 text-base whitespace-nowrap hover:bg-white hover:text-[#1C1C1C] transition-colors"
                >
                  <MessageSquare className="h-5 w-5" strokeWidth={1.75} />
                  Text Us
                </a>
              </div>
            </div>
            <p className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed">
              Starting at $50 per mover, per hour. 2-hour minimum. $25 booking fee
              per job.
            </p>
            <p className="mt-2 text-xs text-neutral-500 max-w-2xl leading-relaxed">
              Duke Summers Moving is a moving labor service provider only. We do
              not transport household goods and are not a licensed motor carrier.
            </p>
          </Reveal>
        </section>

        <HiringBanner />
        <Services />
        <OurPromise />

        {/* Served cities */}
        <section
          data-testid="city-served-areas"
          className="bg-[#262626] border-t border-white/5 py-20 sm:py-28"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal className="max-w-2xl mb-10">
              <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
                Where We Serve
              </p>
              <h2 className="font-display font-bold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl">
                {area?.region}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2.5">
                {area?.cities.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-100 bg-[#303030] border border-[#3d3d3d] px-3.5 py-2"
                  >
                    <MapPin className="h-3.5 w-3.5 text-[#FF4500]" strokeWidth={2} />
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-sm text-neutral-300">
                …and surrounding communities.
              </p>
              <a
                href="/#quote"
                data-testid="city-quote-btn-bottom"
                className="group mt-8 inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#1C1C1C] font-bold px-8 py-4 text-base hover:bg-[#E03C00] transition-colors"
              >
                Request a Quote
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <BottomBar />
    </div>
  );
}
